<template>
    <transition name="debug-bar-slide">
        <div v-if="visible" class="debug-bar-wrapper">
            <!-- Bot Reply Bubble -->
            <transition name="reply-fade">
                <div v-if="status === 'completed' && lastReply" class="debug-reply-bubble">
                    <div class="reply-header">
                        <v-icon size="14" color="#888">mdi-robot-outline</v-icon>
                        <span class="reply-label">Bot Reply</span>
                        <v-tooltip location="top">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props" icon size="x-small" variant="text" class="reply-action-btn" @click="copyReply">
                                    <v-icon size="14">{{ copied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ copied ? 'Copied!' : 'Copy' }}</span>
                        </v-tooltip>
                        <v-tooltip location="top">
                            <template v-slot:activator="{ props }">
                                <v-btn v-bind="props" icon size="x-small" variant="text" class="reply-action-btn" @click="replyExpanded = !replyExpanded">
                                    <v-icon size="14">{{ replyExpanded ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
                                </v-btn>
                            </template>
                            <span>{{ replyExpanded ? 'Collapse' : 'Expand' }}</span>
                        </v-tooltip>
                    </div>
                    <div class="reply-text" :class="{ 'reply-expanded': replyExpanded }">{{ lastReply }}</div>
                </div>
            </transition>

            <!-- Main Debug Card -->
            <div class="debug-card" :class="{ 'card-running': isRunning }">
                <!-- Zone 1: Composer -->
                <div class="composer" :class="{ 'composer-disabled': isRunning || isPaused }">
                    <textarea
                        ref="textarea"
                        v-model="localMessage"
                        :placeholder="inputPlaceholder"
                        :disabled="isRunning || isPaused"
                        class="composer-textarea"
                        rows="1"
                        @input="autoGrow"
                        @keydown="handleKeydown"
                        @focus="textareaFocused = true"
                        @blur="textareaFocused = false"
                    ></textarea>
                    <div class="composer-footer">
                        <label class="keep-memory-toggle" :class="{ 'toggle-disabled': isRunning || isPaused }">
                            <input
                                type="checkbox"
                                class="keep-memory-cb"
                                :checked="keepMemory"
                                :disabled="isRunning || isPaused"
                                @change="$emit('update:keep-memory', $event.target.checked)"
                            />
                            <span class="toggle-label">Keep Memory</span>
                        </label>
                        <v-tooltip v-if="debugUserId" location="top">
                            <template v-slot:activator="{ props }">
                                <span v-bind="props" class="debug-uid" @click.stop="copyUserId">
                                    {{ debugUserId }}
                                    <v-icon size="10" class="uid-copy-icon">{{ uidCopied ? 'mdi-check' : 'mdi-content-copy' }}</v-icon>
                                </span>
                            </template>
                            <span>{{ uidCopied ? 'Copied!' : 'Click to copy User ID' }}</span>
                        </v-tooltip>
                        <span class="composer-spacer"></span>
                        <transition name="hint-fade">
                            <span v-if="showKeyboardHint" class="keyboard-hint">
                                <kbd>Enter</kbd> send &middot; <kbd>Shift+Enter</kbd> new line
                            </span>
                        </transition>
                        <button
                            class="send-btn"
                            :class="{ 'send-active': canSend }"
                            :disabled="!canSend"
                            @click="handleSend"
                        >
                            <v-icon size="16">mdi-send</v-icon>
                        </button>
                    </div>
                </div>

                <!-- Zone 2: Status Strip -->
                <div class="status-strip">
                    <div v-if="isRunning" class="strip-progress"></div>
                    <div class="strip-left">
                        <span class="status-dot" :class="statusDotClass">
                            <v-icon v-if="isRunning" size="8" class="dot-spin">mdi-loading</v-icon>
                        </span>
                        <span class="strip-label">{{ statusText }}</span>
                    </div>
                    <div class="strip-right">
                        <div class="strip-badges" v-if="breakpointCount > 0 || stepCount > 0 || setNextTargetName">
                            <span v-if="setNextTargetName" class="mini-badge badge-setnext" @click="$emit('cancel-set-next')" title="Cancel Set Next Statement">
                                <v-icon size="10" class="mr-1">mdi-arrow-right-bold</v-icon>
                                Return to Flow
                                <v-icon size="10" class="ml-1 cancel-x">mdi-close</v-icon>
                            </span>
                            <span v-if="breakpointCount > 0" class="mini-badge badge-bp" @click.stop="$emit('clear-all-breakpoints')" title="Click to remove all breakpoints">
                                <span class="bp-dot"></span>
                                {{ breakpointCount }}
                                <v-icon size="10" class="ml-1 clear-bp-icon">mdi-close</v-icon>
                            </span>
                            <span v-if="stepCount > 0" class="mini-badge badge-steps">
                                {{ stepCount }} step{{ stepCount > 1 ? 's' : '' }}
                            </span>
                        </div>
                        <span class="strip-sep" v-if="breakpointCount > 0 || stepCount > 0 || setNextTargetName"></span>
                        <div class="strip-controls">
                            <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                    <button
                                        v-bind="props"
                                        class="ctrl-btn ctrl-resume"
                                        :class="{ 'ctrl-enabled': isPaused }"
                                        :disabled="!isPaused"
                                        @click="$emit('resume')"
                                    >
                                        <v-icon size="14">mdi-play</v-icon>
                                    </button>
                                </template>
                                <span>Resume (continue to next breakpoint)</span>
                            </v-tooltip>
                            <v-tooltip location="top">
                                <template v-slot:activator="{ props }">
                                    <button
                                        v-bind="props"
                                        class="ctrl-btn ctrl-stop"
                                        :class="{ 'ctrl-enabled': isRunning || isPaused }"
                                        :disabled="!isRunning && !isPaused"
                                        @click="$emit('stop')"
                                    >
                                        <v-icon size="14">mdi-stop</v-icon>
                                    </button>
                                </template>
                                <span>Stop execution</span>
                            </v-tooltip>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
export default {
    name: 'DebugControlBar',
    props: {
        visible: { type: Boolean, default: false },
        status: { type: String, default: 'ready' },
        pausedNodeName: { type: String, default: '' },
        breakpointCount: { type: Number, default: 0 },
        stepCount: { type: Number, default: 0 },
        totalDuration: { type: Number, default: 0 },
        errorMessage: { type: String, default: '' },
        message: { type: String, default: '' },
        lastReply: { type: String, default: '' },
        setNextTargetName: { type: String, default: '' },
        keepMemory: { type: Boolean, default: false },
        debugUserId: { type: String, default: '' },
    },
    emits: ['send', 'resume', 'stop', 'update:message', 'cancel-set-next', 'update:keep-memory', 'clear-all-breakpoints'],
    data() {
        return {
            localMessage: this.message,
            copied: false,
            uidCopied: false,
            replyExpanded: false,
            textareaFocused: false,
        };
    },
    watch: {
        message(val) { this.localMessage = val; },
        localMessage(val) {
            this.$emit('update:message', val);
            this.$nextTick(() => this.autoGrow());
        },
        lastReply() { this.replyExpanded = false; },
        visible(val) {
            if (val) this.$nextTick(() => this.autoGrow());
        },
    },
    computed: {
        isRunning() { return this.status === 'running'; },
        isPaused() { return this.status === 'paused'; },
        canSend() { return this.localMessage?.trim() && !this.isRunning && !this.isPaused; },
        showKeyboardHint() {
            return this.textareaFocused && this.localMessage && !this.isRunning && !this.isPaused;
        },
        inputPlaceholder() {
            if (this.isRunning) return 'Flow is executing...';
            if (this.isPaused) return `Paused at "${this.pausedNodeName || 'breakpoint'}" - press Resume to continue`;
            return 'Type a test message...';
        },
        statusDotClass() {
            return {
                ready: this.status === 'ready',
                running: this.status === 'running',
                paused: this.status === 'paused',
                completed: this.status === 'completed',
                error: this.status === 'error',
                stopped: this.status === 'stopped',
            };
        },
        statusText() {
            const map = {
                ready: 'Ready',
                running: 'Running...',
                paused: `Paused at "${this.pausedNodeName || 'breakpoint'}"`,
                completed: 'Done',
                error: `Error: ${this.errorMessage || 'Flow execution failed'}`,
                stopped: 'Stopped',
            };
            return map[this.status] || this.status;
        },
    },
    methods: {
        handleSend() {
            if (this.canSend) {
                this.$emit('send');
            }
        },
        handleKeydown(e) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.handleSend();
            }
        },
        autoGrow() {
            this.$nextTick(() => {
                const el = this.$refs.textarea;
                if (el) {
                    el.style.height = 'auto';
                    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                }
            });
        },
        async copyReply() {
            try {
                await navigator.clipboard.writeText(this.lastReply);
                this.copied = true;
                setTimeout(() => { this.copied = false; }, 2000);
            } catch { /* ignore */ }
        },
        async copyUserId() {
            try {
                await navigator.clipboard.writeText(this.debugUserId);
                this.uidCopied = true;
                setTimeout(() => { this.uidCopied = false; }, 2000);
            } catch { /* ignore */ }
        },
    },
};
</script>

<style scoped>
.debug-bar-wrapper {
    position: fixed;
    bottom: 20px;
    left: 120px;
    z-index: 101;
    width: 100vw;
    max-width: 100vw;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    pointer-events: none;
}

.debug-card {
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.06);
    width: 520px;
    max-width: calc(100vw - 40px);
    overflow: hidden;
    pointer-events: auto;
    margin: 0 auto;
}

.composer {
    width: 100%;
    padding: 12px 14px 8px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.composer-disabled { opacity: 0.7; pointer-events: none; }

.composer-textarea {
    width: 100%;
    border: none;
    outline: none;
    resize: none;
    font-size: 14px;
    line-height: 1.5;
    font-family: inherit;
    background: transparent;
    color: #333;
}
.composer-textarea::placeholder { color: #aaa; }

.composer-footer {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid #f0f0f0;
}

.keep-memory-toggle { display: flex; align-items: center; gap: 4px; cursor: pointer; }
.keep-memory-cb { accent-color: #1976d2; }
.toggle-label { font-size: 11px; color: #666; }
.toggle-disabled { opacity: 0.5; cursor: not-allowed; }

.debug-uid {
    font-size: 10px;
    color: #aaa;
    font-family: monospace;
    cursor: pointer;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.uid-copy-icon { opacity: 0.5; margin-left: 2px; }
.debug-uid:hover { color: #666; }

.composer-spacer { flex: 1; }

.keyboard-hint {
    font-size: 10px;
    color: #bbb;
    white-space: nowrap;
}
.keyboard-hint kbd {
    background: #f0f0f0;
    border: 1px solid #ddd;
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 9px;
}

.send-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: #e0e0e0;
    color: #999;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}
.send-btn.send-active { background: #1976d2; color: white; }
.send-btn.send-active:hover { background: #1565c0; }

.status-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 14px;
    background: #fafbfc;
    border-top: 1px solid #f0f0f0;
    position: relative;
    overflow: hidden;
}

.strip-progress {
    position: absolute;
    top: 0;
    left: 0;
    height: 2px;
    background: linear-gradient(90deg, #42a5f5, #7c3aed, #42a5f5);
    background-size: 200% 100%;
    animation: progressSlide 1.5s linear infinite;
}

@keyframes progressSlide {
    to { background-position: 200% 0; }
}

.strip-left { display: flex; align-items: center; }
.strip-right { display: flex; align-items: center; gap: 8px; }

.status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-right: 6px;
}
.status-dot.ready { background: #9e9e9e; }
.status-dot.running { background: #42a5f5; }
.status-dot.paused { background: #ff9800; }
.status-dot.completed { background: #4caf50; }
.status-dot.error { background: #f44336; }
.status-dot.stopped { background: #757575; }

.dot-spin { animation: dotSpin 1s linear infinite; }
@keyframes dotSpin { to { transform: rotate(360deg); } }

.strip-label { font-size: 12px; color: #555; font-weight: 500; }

.strip-badges { display: flex; align-items: center; gap: 6px; }
.mini-badge {
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 8px;
    font-weight: 600;
    display: flex;
    align-items: center;
}
.badge-setnext { background: #fff3e0; color: #e65100; cursor: pointer; }
.badge-setnext:hover { background: #ffe0b2; }
.badge-bp { background: #ffebee; color: #c62828; cursor: pointer; }
.badge-bp:hover { background: #ffcdd2; }
.bp-dot { width: 6px; height: 6px; border-radius: 50%; background: #c62828; margin-right: 3px; }
.clear-bp-icon { opacity: 0.6; }
.badge-bp:hover .clear-bp-icon { opacity: 1; }
.badge-steps { background: #e3f2fd; color: #1565c0; }

.strip-sep { width: 1px; height: 16px; background: #e0e0e0; }

.strip-controls { display: flex; gap: 4px; }
.ctrl-btn {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    border: 1px solid #e0e0e0;
    background: #fff;
    color: #bbb;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
}
.ctrl-btn.ctrl-enabled { color: #333; border-color: #bbb; }
.ctrl-btn.ctrl-enabled:hover { background: #f5f5f5; }
.ctrl-btn.ctrl-resume.ctrl-enabled { color: #1976d2; border-color: #90caf9; }
.ctrl-btn.ctrl-resume.ctrl-enabled:hover { background: #e3f2fd; }
.ctrl-btn.ctrl-stop.ctrl-enabled { color: #d32f2f; border-color: #ef9a9a; }
.ctrl-btn.ctrl-stop.ctrl-enabled:hover { background: #ffebee; }

.debug-reply-bubble {
    background: #fff;
    border-radius: 12px;
    padding: 10px 14px;
    width: 500px;
    max-width: calc(100vw - 40px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border: 1px solid #e8e8e8;
    pointer-events: auto;
    margin: 0 auto;
}

.reply-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
}
.reply-label { font-size: 11px; font-weight: 600; color: #888; }
.reply-action-btn { opacity: 0.5; }
.reply-action-btn:hover { opacity: 1; }

.reply-text {
    font-size: 13px;
    color: #333;
    line-height: 1.5;
    max-height: 80px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
}
.reply-text.reply-expanded { max-height: 300px; overflow-y: auto; }

.debug-bar-slide-enter-active,
.debug-bar-slide-leave-active { transition: all 0.3s ease; }
.debug-bar-slide-enter-from,
.debug-bar-slide-leave-to { opacity: 0; transform: translateY(30px); }

.reply-fade-enter-active,
.reply-fade-leave-active { transition: all 0.25s ease; }
.reply-fade-enter-from,
.reply-fade-leave-to { opacity: 0; transform: translateY(10px); }

.hint-fade-enter-active,
.hint-fade-leave-active { transition: opacity 0.2s; }
.hint-fade-enter-from,
.hint-fade-leave-to { opacity: 0; }
</style>